import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { selectBrokerConfigurationState } from 'src/redux/broker_configuration/selected';
import { fetchBrokerAnalizisThunk, fetchBrokerProperties, saveBrokerConfigurationThunk } from 'src/redux/broker_configuration/thunks';
import { useEffect, useMemo, useState } from 'react';
import { typesPropertiesOptions } from 'src/components/QualifyPropertyHabi/constants';
import {
    OrphanAssignmentHookReturn
} from '../types';
import { resetValues, setPropertiesTypes, setTransactionType } from 'src/redux/broker_configuration/slice';

export const useOrphanAssigment = (): OrphanAssignmentHookReturn => {
    const [showCarouselTable, setShowCarouselTable] = useState(false);
    const [shouldFilterOnEmpty, setShouldFilterOnEmpty] = useState(false);
    const [modalState, setModalState] = useState({
        isOpen: false,
        type: 'confirmation' as 'confirmation' | 'success' | 'error',
        title: '',
        message: '',
        onConfirm: null as (() => void) | null
    });
    const {
        brokersProperties: data,
        brokersPropertiesFiltered: dataFiltered,
        actualBrokerConfiguration: { purpose, type_childrens },
        analysis: carousels,
        loading,
        showConfiguration,
        configurations,
        saving,
        showTables,
        error,
        showEmptyData
    } = useAppSelector(selectBrokerConfigurationState);

    const { type_childrens: propertyTypesConfig, purpose: transactionTypeConfig, userEmail } = configurations
    const dispatch = useAppDispatch()

    const handleCheckboxPropertyTypes = (value: string): void => {
        let arrayPropertyTypes = propertyTypesConfig ? propertyTypesConfig.split(",") : [];
        if (arrayPropertyTypes?.includes(value)) {
            arrayPropertyTypes = arrayPropertyTypes.filter(item => item !== value);
        } else {
            arrayPropertyTypes.push(value);
        }
        const stringOfArrayPropertyTypes = arrayPropertyTypes.join(",");
        dispatch(setPropertiesTypes(stringOfArrayPropertyTypes));
    };

    const handleCheckboxTransactionTypes = (value: string): void => {
        let arrayTransactionTypes = transactionTypeConfig ? transactionTypeConfig.split(",") : [];
        if (arrayTransactionTypes?.includes(value)) {
            arrayTransactionTypes = arrayTransactionTypes.filter(item => item !== value);
        } else {
            arrayTransactionTypes.push(value);
        }
        const stringOfArrayTransactionTypes = arrayTransactionTypes.join(",");
        dispatch(setTransactionType(stringOfArrayTransactionTypes));
    };

    const propertyTypes = useMemo(() => {
        return (data?.properties_by_type || []).map((item, idx) => {
            const propertyType = typesPropertiesOptions.find(option => option.subType === item.type_children);
            return {
                label: propertyType?.label,
                configuration: propertyTypesConfig?.includes(String(item.type_children)),
                count: item.total,
                type_children: String(item.type_children)
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data?.properties_by_type]);

    const transactionTypes = useMemo(() => [
        {
            label: 'Venta',
            configuration: transactionTypeConfig?.includes('1'),
            count: data?.properties_for_sale || 0,
            countFiltered: dataFiltered?.properties_for_sale || 0,
            purpose: '1'
        },
        {
            label: 'Renta',
            configuration: transactionTypeConfig?.includes('2'),
            count: data?.properties_for_rent || 0,
            countFiltered: dataFiltered?.properties_for_rent || 0,
            purpose: '2'
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
    ], [data, dataFiltered]);

    const prices = useMemo(() => [
        {
            transaction: 'Venta',
            range: `${data?.sale_price_range?.min_price || 0} - ${data?.sale_price_range?.max_price || 0}`,
            count: data?.properties_for_sale || 0,
            filteredRange: `${dataFiltered?.sale_price_range?.min_price || 0} - ${dataFiltered?.sale_price_range?.max_price || 0}`,
            countFiltered: dataFiltered?.properties_for_sale || 0,
        },
        {
            transaction: 'Renta',
            range: `${data?.rental_price_range?.min_price || 0} - ${data?.rental_price_range?.max_price || 0}`,
            count: data?.properties_for_rent || 0,
            filteredRange: `${dataFiltered?.rental_price_range?.min_price || 0} - ${dataFiltered?.rental_price_range?.max_price || 0}`,
            countFiltered: dataFiltered?.properties_for_rent || 0,
        },
    ], [data, dataFiltered]);

    const locations = useMemo(() => {
        if (dataFiltered?.properties_by_sepomex?.length > 0) {
            return (dataFiltered?.properties_by_sepomex).map((item, idx) => ({
                label: `${item.colony}, ${item.city}, ${item.state}`,
                count: item.total,
            }));
        } else {
            return (data.properties_by_sepomex).map((item, idx) => ({
                label: `${item.colony}, ${item.city}, ${item.state}`,
                count: item.total,
            }));
        }

    }, [data, dataFiltered]);


    const showModal = (type: 'confirmation' | 'success' | 'error', title: string, message: string, onConfirm?: () => void) => {
        setModalState({
            isOpen: true,
            type,
            title,
            message,
            onConfirm: onConfirm || null
        });
    };

    const closeModal = () => {
        setModalState(prev => ({ ...prev, isOpen: false }));
    };

    const handleSearch = async (data: any) => {
        setShowCarouselTable(false)
        setShouldFilterOnEmpty(false)
        const payload = {
            user_email: data?.list,
            filter: false
        }
        dispatch(resetValues())
        await dispatch(fetchBrokerProperties(payload))
    };

    const handleCheckbox = (table: string, value?: string) => {
        setShouldFilterOnEmpty(true);
        switch (table) {
            case 'propertyType':
                if (value) handleCheckboxPropertyTypes(value);
                break;
            case 'transactionType':
                if (value) handleCheckboxTransactionTypes(value);
                break;
            default:
                break;
        }
    };

    const filterData = () => {
        const payload = {
            user_email: userEmail,
            type_childrens: propertyTypesConfig,
            purpose: transactionTypeConfig,
            filter: true
        }
        dispatch(fetchBrokerProperties(payload))
    }

    const handleSaveConfiguration = async () => {
        showModal(
            'confirmation',
            'Confirmar guardado',
            '¿Estás seguro de que deseas guardar esta configuración?',
            async () => {
                const payload = {
                    purpose: transactionTypeConfig,
                    type_childrens: propertyTypesConfig,
                    user_id: data.user_id
                }
                try {
                    await dispatch(saveBrokerConfigurationThunk(payload)).unwrap();
                    showModal(
                        'success',
                        'Configuración guardada',
                        'La configuración se ha guardado exitosamente.'
                    );
                } catch (error) {
                    showModal(
                        'error',
                        'Error al guardar',
                        'Ha ocurrido un error al guardar la configuración. Por favor, inténtalo de nuevo.'
                    );
                }
            }
        );
    }

    const handleAnalyze = () => {
        const payload = {
            purpose: transactionTypeConfig,
            type_childrens: propertyTypesConfig,
            users_id: data.user_id
        }
        showModal(
            'confirmation',
            'Confirmar análisis',
            '¿Estás seguro de que deseas construir los carruseles con la información seleccionada?',
            async () => {
                closeModal()
                try {
                    await dispatch(fetchBrokerAnalizisThunk(payload))
                    setShowCarouselTable(true);
                    showModal(
                        'success',
                        'Análisis finalizado con éxito',
                        'Se pudo realizar un análisis de datos.'
                    );
                } catch (error) {
                    showModal(
                        'error',
                        'Error al hace análisis',
                        'Ha ocurrido un error al hacer el análisis con la configuración actual. Por favor, inténtalo de nuevo.'
                    );
                }
            }
        );
    };

    useEffect(() => {
        if ((userEmail !== "" && (propertyTypesConfig !== "" || transactionTypeConfig !== "")) || 
            (userEmail !== "" && propertyTypesConfig === "" && transactionTypeConfig === "" && shouldFilterOnEmpty)) {
            filterData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [propertyTypesConfig, transactionTypeConfig, shouldFilterOnEmpty])

    // Efecto para hacer scroll automático cuando se muestre la tabla de carruseles
    useEffect(() => {
        if (showCarouselTable) {
            const carouselTable = document.getElementById('carousel-table');
            if (carouselTable) {
                carouselTable.scrollIntoView({
                    behavior: 'smooth',
                    block: 'start'
                });
            }
        }
    }, [showCarouselTable]);


    useEffect(() => {
        if (!showTables && error) {
            showModal(
                "error",
                "Error al cargar los datos",
                "Se presentó un error al consultar los datos del usuario. Por favor, inténtalo de nuevo.")
        }
    }, [showTables, error])

    return {
        data,
        loading,
        saving,
        showTables,
        propertyTypes,
        transactionTypes,
        propertyTypesConfig,
        transactionTypeConfig,
        prices,
        locations,
        showConfiguration,
        showCarouselTable,
        purpose,
        type_childrens,
        modalState,
        carousels,
        showEmptyData,
        handleSearch,
        handleCheckbox,
        handleSaveConfiguration,
        handleAnalyze,
        closeModal
    };
};